import React from 'react';
import { css } from '@emotion/react';

import { generatePath, matchPath, useLocation, useHistory } from 'react-router-dom';

import DashboardLayoutBody from '@opendoor/bricks/deprecated/UNSAFE_layout/DashboardLayoutBody';
import {
  DashboardLayoutWrapper,
  IDashboardLayoutWrapperAccountInfo,
  IDashboardLayoutWrapperProps,
} from 'sellDirect/components/legacy/layout/DashboardLayoutWrapper';

import { HOMEPAGE_URL, NAVIGATION_HEIGHT } from 'shared/helpers/globals';
import { CakeFooter } from 'sellDirect/components/legacy/layout/CakeFooter';
import { usePath } from 'shared/helpers/hooks/usePath';
import { NavBar, useNavBarLinks } from 'sellDirect/components/legacy/layout/NavBar';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { getHubOverviewRoute } from 'shared/helpers/routes';
import { useOfferContext } from 'sellDirect/OfferContext';
import { Box, Image } from '@opendoor/bricks/core';
import { useAuth } from '@opendoor/auth-fe';
import { ExternalLink } from 'platform/react-router';

export interface ICakeLayoutProps {
  loading?: boolean;
  hideNavBar?: boolean;
  headerType?: IDashboardLayoutWrapperProps['headerType'];
  hideFooter?: boolean;
  renderContent: () => React.ReactNode;
  selectedNavBarItem?: string;
  renderCustomFooter?: () => React.ReactNode;
}

export interface IProps extends ICakeLayoutProps {
  isSellDirectQualified?: boolean;
  shouldSeeAgentExperience?: boolean;
  isLoadingAccountInfo?: boolean;
  accountInfo?: IDashboardLayoutWrapperAccountInfo;
  addressUUID: string | undefined;
  onLogout: () => void;
}

/** TODO: Add documentation */
export const CakeLayoutWithoutContext: React.FC<IProps> = ({
  loading,
  headerType,
  hideNavBar = false,
  hideFooter = false,
  selectedNavBarItem,
  renderContent,
  renderCustomFooter,
  isSellDirectQualified = false,
  isLoadingAccountInfo = false,
  shouldSeeAgentExperience = false,
  accountInfo,
  onLogout,
  addressUUID,
}) => {
  const history = useHistory();
  const location = useLocation();
  const path = usePath();
  const navBarItems = useNavBarLinks();
  const onParentRoute = !!navBarItems.find(({ link }) =>
    // If one of the side-drawer nav-items (ie navBarItems) has a path that matches
    // the current pathname, then the current route is a parent route.
    matchPath(location.pathname, link),
  );
  const onChildRoute = !onParentRoute && navBarItems.length;
  const showNavBar = !hideNavBar;

  /** TODO: Add documentation */
  const renderNavBar = () => (
    <NavBar navBarItems={navBarItems} selectedNavBarItem={selectedNavBarItem ?? path} />
  );

  /** TODO: Add documentation */
  const getOnBackFunction = () => {
    const showBackButton = isSellDirectQualified && onChildRoute && showNavBar;

    const agentSSVAFlow = shouldSeeAgentExperience && path === 'virtual-assessment/confirmation';

    if (agentSSVAFlow) {
      return () => history.push(generatePath(getHubOverviewRoute(), { addressUUID }));
    }
    return showBackButton ? () => history.goBack() : undefined;
  };

  return (
    <DashboardLayoutWrapper
      onBackClick={getOnBackFunction()}
      accountInfo={accountInfo}
      headerType={headerType}
      footerType="NONE"
      isLoadingAccountInfo={isLoadingAccountInfo}
      onLogout={onLogout}
      renderNav={loading || hideNavBar ? undefined : renderNavBar}
      renderLogo={RenderLogo}
      style={{ dashboardLayoutWrapperChildrenContainer }}
      analyticsName="cake-legacy"
    >
      <DashboardLayoutBody
        renderContent={() => (
          <Box>
            {renderContent()}
            {!loading && !hideFooter && <CakeFooter renderCustomFooter={renderCustomFooter} />}
          </Box>
        )}
      />
    </DashboardLayoutWrapper>
  );
};

/** TODO: Add documentation */
export const CakeLayout: React.FC<ICakeLayoutProps> = (props) => {
  const { isSellDirectQualified, addressUUID } = useQualificationContext();
  const { shouldSeeAgentExperience } = useOfferContext();
  const { authentication, user, logout } = useAuth();
  return (
    <CakeLayoutWithoutContext
      {...props}
      addressUUID={addressUUID}
      isSellDirectQualified={isSellDirectQualified}
      shouldSeeAgentExperience={shouldSeeAgentExperience}
      isLoadingAccountInfo={authentication.state === 'authenticating'}
      accountInfo={
        user
          ? {
              email: user.email,
              name: user.firstName || user.fullName,
              leads: [],
            }
          : undefined
      }
      onLogout={logout}
    />
  );
};

const RenderLogo = () => {
  return (
    <Box lineHeight={1}>
      <ExternalLink
        analyticsName="cake-layout-logo-link"
        href={HOMEPAGE_URL}
        aria-label={"Navigate to Opendoor's homepage"}
      >
        <Image
          display="block"
          margin="30px 0"
          src="https://imgdrop.imgix.net/logo-opendoor.svg"
          alt="Opendoor logo"
          height="22"
          aria-label="Navigate to Opendoor's home page"
          aria-hidden
        />
      </ExternalLink>
    </Box>
  );
};

const dashboardLayoutWrapperChildrenContainer = css`
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  min-height: calc(100vh - ${NAVIGATION_HEIGHT});

  .row {
    width: 100%;
    margin: auto;
  }

  .od-dashboard-layout-body__center {
    padding: 0;
  }

  .container {
    width: 100%;
  }
`;
